import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info, X, Euro, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Button } from './ui/Button';
interface SlideFastCalculatorProps {
  onContinue: () => void;
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-603" data-mp="30n548ycxtjb5">
      <div className="flex items-center justify-between" data-id="element-604" data-mp="30jzctcgu65c9">
        <label className="text-sm font-medium text-slate-600" data-id="element-605" data-mp="2mnvlps19ed40">{label}</label>
        <input type="number" value={value} onChange={e => {
        const v = Number(e.target.value);
        if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
      }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-606" data-mp="2rlx5kf6ieggj" />
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-607" data-mp="3jlt4tyyc1fjl" />
    </div>;
}
type InfoKey = 'master' | 'localization' | 'insights' | null;
interface InfoPopoverProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  title: string;
  body: string;
}
function InfoPopover({
  isOpen,
  onToggle,
  onClose,
  title,
  body
}: InfoPopoverProps) {
  return <span className="relative inline-flex" data-id="element-608" data-mp="gvaoep95fbyo">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-label={title} className="w-5 h-5 rounded-full bg-[#6C2BD9]/[0.08] hover:bg-[#6C2BD9]/[0.16] border border-[#6C2BD9]/30 text-[#6C2BD9] flex items-center justify-center transition-colors" data-id="element-609" data-mp="lrhow2irzv98">
        <Info className="w-3 h-3" data-id="element-610" data-mp="225hmh6u5f8tb" />
      </button>
      <AnimatePresence data-id="element-611" data-mp="228vlq67in1wj">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.15
      }} className="absolute z-30 top-full left-0 mt-2 w-[280px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-612" data-mp="26736dc53ond0">
            <div className="flex items-start justify-between gap-3" data-id="element-613" data-mp="32zszy2zxshwg">
              <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-614" data-mp="2yy7is5gddhgg">
                {title}
              </p>
              <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-615" data-mp="2u1h4nno4kvxo">
                <X className="w-3.5 h-3.5" data-id="element-616" data-mp="s6owqu5rd5gk" />
              </button>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-617" data-mp="3l0x6hwenryxu">
              {body}
            </p>
          </motion.div>}
      </AnimatePresence>
    </span>;
}
export function SlideFastCalculator({
  onContinue
}: SlideFastCalculatorProps) {
  const [products, setProducts] = useState(1);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(2);
  const [countries, setCountries] = useState(1);
  const [benchmarkOpen, setBenchmarkOpen] = useState(false);
  const [costBenchmarkOpen, setCostBenchmarkOpen] = useState(false);
  const [unitsOpen, setUnitsOpen] = useState(false);
  const [costOpen, setCostOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState<InfoKey>(null);
  // List-price unit economics
  const masterSnakCostBase = 2900;
  const masterSnakCostFloor = 1900;
  const campaignDuration = 3;
  const localizationCostBase = 999;
  const monthlyInsightsFeeBase = 99;
  const durationRebate = 0;
  const FOLLOW_UP_MASTER_COST = 1000;
  // --- Volume discounts ---
  const COUNTRY_DISCOUNT_FLOOR = 2;
  const COUNTRY_DISCOUNT_CEILING = 50;
  const COUNTRY_DISCOUNT_MAX = 0.5;
  const countryDiscount = Math.min(COUNTRY_DISCOUNT_MAX, Math.max(0, (countries - COUNTRY_DISCOUNT_FLOOR) / (COUNTRY_DISCOUNT_CEILING - COUNTRY_DISCOUNT_FLOOR) * COUNTRY_DISCOUNT_MAX));
  const effectiveCountryCost = localizationCostBase * (1 - countryDiscount);
  const countryDeploymentCost = effectiveCountryCost;
  const totalAnnualCampaigns = products * campaignsPerProduct;
  const MASTER_DISCOUNT_FLOOR = 1;
  const MASTER_DISCOUNT_CEILING = 100;
  const masterProgress = Math.min(1, Math.max(0, (totalAnnualCampaigns - MASTER_DISCOUNT_FLOOR) / (MASTER_DISCOUNT_CEILING - MASTER_DISCOUNT_FLOOR)));
  const effectiveMasterSnakCost = masterSnakCostBase - (masterSnakCostBase - masterSnakCostFloor) * masterProgress;
  const masterSnakCost = effectiveMasterSnakCost;
  const INSIGHTS_DISCOUNT_FLOOR = 2;
  const INSIGHTS_DISCOUNT_CEILING = 50;
  const INSIGHTS_DISCOUNT_MAX = 0.5;
  const insightsDiscount = Math.min(INSIGHTS_DISCOUNT_MAX, Math.max(0, (countries - INSIGHTS_DISCOUNT_FLOOR) / (INSIGHTS_DISCOUNT_CEILING - INSIGHTS_DISCOUNT_FLOOR) * INSIGHTS_DISCOUNT_MAX));
  const effectiveMonthlyInsightsFee = monthlyInsightsFeeBase * (1 - insightsDiscount);
  const monthlyInsightsFee = effectiveMonthlyInsightsFee;
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const hasMaster = masterSnakCost > 0;
    const firstCampaigns = hasMaster ? products : 0;
    const followUpCampaigns = hasMaster ? Math.max(0, totalCampaigns - products) : 0;
    const annualMasterSnakCost = hasMaster ? firstCampaigns * masterSnakCost + followUpCampaigns * FOLLOW_UP_MASTER_COST : 0;
    const deploymentPerCampaign = countryDeploymentCost * countries;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration * countries;
    const rawCostFirstCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    const rawCostFollowUp = FOLLOW_UP_MASTER_COST + deploymentPerCampaign + insightsPerCampaign;
    const rawCostRollout = deploymentPerCampaign + insightsPerCampaign;
    const rawCostPerCampaign = hasMaster ? rawCostFirstCampaign : rawCostRollout;
    const totalBeforeRebate = hasMaster ? firstCampaigns * rawCostFirstCampaign + followUpCampaigns * rawCostFollowUp : totalCampaigns * rawCostRollout;
    const totalAnnualCost = totalBeforeRebate * (1 - durationRebate);
    const costPerCampaign = totalCampaigns > 0 ? totalAnnualCost / totalCampaigns : 0;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      firstCampaigns,
      followUpCampaigns,
      rawCostPerCampaign,
      costPerCampaign,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, campaignDuration]);
  return <div className="h-full w-full overflow-y-auto bg-white text-slate-900 font-sans" data-id="element-618" data-mp="2b7r5vu43zsp6">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 md:py-8" data-id="element-619" data-mp="1wimm48ho1gey">
        {/* Header */}
        <div className="mb-6" data-id="element-620" data-mp="21h66w5kiwm5q">
          <div className="flex items-center gap-3 mb-1" data-id="element-621" data-mp="21geaisj3qkee">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-622" data-mp="2p70hshla80nc">
              Snakker
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-623" data-mp="2u0uzjfhbu5js">
            See for yourself — move the sliders to see the{' '}
            <span className="text-[#6C2BD9]" data-id="element-624" data-mp="djx13bcdrfy6">potential</span>
          </h1>


          {/* Industry benchmark + Cost benchmark popovers */}
          <div className="mt-3 flex flex-wrap items-start gap-2" data-id="element-625" data-mp="sbfed1duuwdu">
            <div className="relative inline-block" data-id="element-626" data-mp="nb1g2b2230t0">
              <button type="button" onClick={() => {
              setBenchmarkOpen(v => !v);
              setCostBenchmarkOpen(false);
            }} aria-expanded={benchmarkOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-627" data-mp="3vq2yn8fbi2gm">
                <Info className="w-3.5 h-3.5" data-id="element-628" data-mp="1k2v6tc23fdbz" />
                Industry benchmark
              </button>
              <AnimatePresence data-id="element-629" data-mp="1g1jf90znsi15">
                {benchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[380px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-630" data-mp="iatzcth4aqvx">
                    <div className="flex items-start justify-between gap-3" data-id="element-631" data-mp="1kwvsw1dmyyqp">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-632" data-mp="1fyu8nd4h4tdn">
                        Big Pharma at a glance
                      </p>
                      <button type="button" onClick={() => setBenchmarkOpen(false)} aria-label="Close benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-633" data-mp="1shpv9fst68bi">
                        <X className="w-3.5 h-3.5" data-id="element-634" data-mp="14k0oqfdeg8z3" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-635" data-mp="19ibyyr7lwlhu">
                      Big pharma typically runs{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-636" data-mp="1egdg36lh2aqo">
                        20–60 brands
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-637" data-mp="2pxvbniswg424">
                        100–135 countries
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-638" data-mp="utx9y1gm23za">
                        4–6 campaigns/year
                      </span>
                      .
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="relative inline-block" data-id="element-639" data-mp="13s9buzmxgo71">
              <button type="button" onClick={() => {
              setCostBenchmarkOpen(v => !v);
              setBenchmarkOpen(false);
            }} aria-expanded={costBenchmarkOpen} className="hidden items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-640" data-mp="ywlmok8g3v1j">
                <Euro className="w-3.5 h-3.5" data-id="element-641" data-mp="3okchh9ba301h" />
                Cost benchmark
              </button>
              <AnimatePresence data-id="element-642" data-mp="3jmayeodur0q0">
                {costBenchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[420px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-643" data-mp="3jjx2yef9fmhx">
                    <div className="flex items-start justify-between gap-3" data-id="element-644" data-mp="2dupss4taj1vc">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-645" data-mp="xcscgfz94yrw">
                        Cost Benchmark — Digital Pharma Campaigns
                      </p>
                      <button type="button" onClick={() => setCostBenchmarkOpen(false)} aria-label="Close cost benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-646" data-mp="1hnwg8h29s23g">
                        <X className="w-3.5 h-3.5" data-id="element-647" data-mp="13xjb0mgrj1ht" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-648" data-mp="20juym65jalxa">
                      A typical digital campaign for a legacy brand in one
                      country runs{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-649" data-mp="1rndo954zus2l">
                        €15,000 – €19,000
                      </span>
                      .
                    </p>
                    <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-650" data-mp="g7f8yjj839sx">
                      Compare against Snakker's cost per localized deployment.
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-651" data-mp="602vy41pz81q">
          {/* Inputs */}
          <div className="space-y-5" data-id="element-652" data-mp="az736p532vpf">
            <button type="button" onClick={() => setUnitsOpen(v => !v)} aria-expanded={unitsOpen} className="w-full flex items-center gap-3 rounded-2xl border-2 border-[#6C2BD9]/20 bg-white px-5 py-4 text-left hover:bg-[#6C2BD9]/[0.04] transition-colors" data-id="element-653" data-mp="fsidx1d0nwmm">
              <SlidersHorizontal className="w-5 h-5 text-[#6C2BD9] shrink-0" data-id="element-654" data-mp="tt2xw0uo3yhp" />
              <span className="flex-1 flex flex-wrap items-baseline gap-x-2" data-id="element-655" data-mp="yr4emf4mflq0">
                <span className="text-base md:text-lg font-bold text-slate-900 tracking-tight" data-id="element-656" data-mp="2vjebdavbb79v">
                  Unit economics
                </span>
                <span className="text-sm text-slate-400" data-id="element-657" data-mp="30ir8cofwduq6">
                  Fixed Snakker pricing behind the numbers
                </span>
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${unitsOpen ? 'rotate-180' : ''}`} data-id="element-658" data-mp="2niyiqtbosuls" />
            </button>

            <AnimatePresence initial={false} data-id="element-659" data-mp="2xgmhrvgbso9r">
              {unitsOpen && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.25
            }} className="overflow-hidden" data-id="element-660" data-mp="2xiphx8b6zxs7">
                  <div className="bg-[#6C2BD9]/[0.07] rounded-2xl p-5 space-y-4 border-2 border-[#6C2BD9]/20" data-id="element-661" data-mp="3q5jyhu4w2d0e">
              {/* Master Snak */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-662" data-mp="cphsp5ssuegt">
                <div className="flex items-center justify-between" data-id="element-663" data-mp="hnj7hej6zrmh">
                  <div className="flex items-center gap-2" data-id="element-664" data-mp="3ni7006uam7j">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-665" data-mp="3n8hsetmcj2w">
                      Master Snak
                    </p>
                    <InfoPopover isOpen={infoOpen === 'master'} onToggle={() => setInfoOpen(infoOpen === 'master' ? null : 'master')} onClose={() => setInfoOpen(null)} title="What is a Master Snak?" body="We start by creating a Master Snak presentation — the original parent version of the campaign. All localized versions are derived from this single source of truth." data-id="element-666" data-mp="3q32z6947t1zf" />
                  </div>
                  <div className="text-right" data-id="element-667" data-mp="3c2jrj33nvxxs">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-668" data-mp="3rlutl3i5w3rc">
                      €{Math.round(effectiveMasterSnakCost).toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-669" data-mp="3ijbsanu0xz77">
                  Per campaign master creation
                </p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-670" data-mp="3dnwic6z50ng2">
                  Volume benefit: drops from €2,900 to €1,900 as total campaigns
                  / year scales from 1 to 100+.
                </p>
              </div>

              {/* Cost per country localization */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-671" data-mp="2yub637ej83jj">
                <div className="flex items-center justify-between" data-id="element-672" data-mp="2uszdct0d3274">
                  <div className="flex items-center gap-2" data-id="element-673" data-mp="2puo3iia2grow">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-674" data-mp="2kx653ltoduv5">
                      Cost per country localization
                    </p>
                    <InfoPopover isOpen={infoOpen === 'localization'} onToggle={() => setInfoOpen(infoOpen === 'localization' ? null : 'localization')} onClose={() => setInfoOpen(null)} title="What is Localization?" body="Localization adapts the Master version to local usage, language and regulatory requirements — so the campaign is market-ready in every country it ships to." data-id="element-675" data-mp="10ec226yyz6cs" />
                  </div>
                  <div className="text-right" data-id="element-676" data-mp="qn7n379anlhy">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-677" data-mp="vgsnf5yd0rkb">
                      €{Math.round(effectiveCountryCost)}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-678" data-mp="2y5681mhhwoi">
                  One-time set-up per country
                </p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-679" data-mp="36lqsk4uobar6">
                  Volume benefit: discount slides from 0% at 2 countries up to
                  50% at 50+ countries.
                </p>
              </div>

              {/* Data insights & hosting */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-680" data-mp="3kie0f2o5zlbv">
                <div className="flex items-center justify-between" data-id="element-681" data-mp="2nuhk7ywn2s16">
                  <div className="flex items-center gap-2" data-id="element-682" data-mp="2rtqd71juulvo">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-683" data-mp="2rs5eczqicgs0">
                      Country activation and insights
                    </p>
                    <InfoPopover isOpen={infoOpen === 'insights'} onToggle={() => setInfoOpen(infoOpen === 'insights' ? null : 'insights')} onClose={() => setInfoOpen(null)} title="What are Data Insights?" body="Based on individual HCP engagement, we bi-monthly deliver not only real-world data but also actionable suggestions for improvement — so every campaign keeps getting sharper." data-id="element-684" data-mp="31lcsb8zbd53p" />
                  </div>
                  <div className="text-right" data-id="element-685" data-mp="1nlhvjtarllef">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-686" data-mp="1nmsvfi13a909">
                      €{effectiveMonthlyInsightsFee.toFixed(0)}
                      <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-687" data-mp="1dun8impp2hrx">
                        / month
                      </span>
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-688" data-mp="13xsb6755qnf8">Per country</p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-689" data-mp="zvnjhdy9s0v8">
                  Volume benefit: discount slides from 0% at 2 countries up to
                  50% at 50+ countries.
                </p>
              </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Portfolio Scale */}
            <div className="bg-[#6C2BD9]/[0.05] rounded-2xl p-5 space-y-5 border border-[#6C2BD9]/15" data-id="element-690" data-mp="ba91pi87m182">
              <div data-id="element-691" data-mp="g93sp7gbyw39">
                <h3 className="text-xs font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-692" data-mp="g8kadpmrq5op">
                  Portfolio Scale
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-693" data-mp="14114iyjs5o4d">
                  Move the sliders — revenue updates instantly.
                </p>
              </div>
              <SliderInput label="Number of Brands" value={products} onChange={setProducts} min={1} max={40} data-id="element-694" data-mp="u8bu86zyugam" />
              <SliderInput label="Campaigns per Year" value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={2} max={12} data-id="element-695" data-mp="1mx0opn6innde" />
              <SliderInput label="Number of Countries" value={countries} onChange={setCountries} min={1} max={130} data-id="element-696" data-mp="3cctfn2ualmli" />
            </div>
          </div>

          {/* Output */}
          <motion.div key={calc.totalAnnualCost} initial={{
          opacity: 0.7
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-5" data-id="element-697" data-mp="3h6xnrpfao0o6">
            {/* Main number */}
            <div className="bg-white rounded-2xl p-6 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-698" data-mp="2xhncqoqhu3we">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2" data-id="element-699" data-mp="hppxlrwof8ig">
                Cost per Localized Campaign
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-700" data-mp="ctsl5wojamay">
                {formatEuro(calc.costPerDeployment)}
              </p>
              <p className="text-sm text-slate-400 mt-1" data-id="element-701" data-mp="1d17mm5n50sg7">
                per localized deployment
              </p>
            </div>

            {/* Volume + total annual */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-702" data-mp="1mzwur6p8eyj9">
              <p className="text-[11px] font-semibold text-[#6C2BD9] uppercase tracking-widest mb-3" data-id="element-703" data-mp="1r18lxgnr7rtk">
                Campaign Volume
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-704" data-mp="1vxh0osy1hj07">
                <div data-id="element-705" data-mp="20rbg3k6m3bth">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-706" data-mp="20qs4smbgvlof">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-707" data-mp="2aog3tog3vfce">
                    master campaigns / year
                  </p>
                </div>
                <div data-id="element-708" data-mp="2equirxgnluye">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-709" data-mp="2enyocj40loep">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-710" data-mp="3v6qk99a7urae">
                    localized deployments / year
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#6C2BD9]/10 flex items-center justify-between gap-4 min-h-[36px]" data-id="element-711" data-mp="3q98ka8e6fmej">
                {costOpen && <p className="text-xs text-slate-500" data-id="element-712" data-mp="mpdptahpbguq">Estimated cost</p>}
                <div className="ml-auto flex items-center gap-3" data-id="element-713" data-mp="hsnbospgivby">
                  {costOpen && <motion.p initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-714" data-mp="czkp54j3km59">
                      {formatEuro(calc.totalAnnualCost)}
                      <span className="text-xs text-slate-400 font-normal ml-1" data-id="element-715" data-mp="2v0o7sr0ulh86">
                        / year
                      </span>
                    </motion.p>}
                  <button type="button" onClick={() => setCostOpen(v => !v)} aria-pressed={costOpen} aria-label={costOpen ? 'Hide estimated cost' : 'Reveal estimated cost'} title={costOpen ? 'Hide estimated cost' : 'Reveal estimated cost'} className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${costOpen ? 'text-[#6C2BD9] bg-[#6C2BD9]/[0.08]' : 'text-slate-300 hover:text-[#6C2BD9] hover:bg-[#6C2BD9]/[0.08]'}`} data-id="element-716" data-mp="2l6edv4w9cavc">
                    <Euro className="w-3.5 h-3.5" data-id="element-717" data-mp="5g1kp9aovq3n" />
                  </button>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed px-1" data-id="element-718" data-mp="fix4u9q4bi80">
              Land with a pilot brand in a few countries, expand across the
              portfolio. Same unit economics at every scale.
            </p>
          </motion.div>
        </div>

        {/* Continue button */}
        <div className="mt-6 flex justify-end" data-id="element-719" data-mp="jl1wx40x46sj"></div>
      </div>
    </div>;
}